import { swSettings } from "app/common/services/swSettings";
import type {
    ContactsEnrichmentSelectedColumns,
    EnrichedState,
    EnrichmentConfigItem,
    PostContactsCsvPropsFormData,
} from "app/pages/sales-intelligence/modules/enrichment/store/type";
import { createCancelable } from "app/pages/workspace/sales/helpers";
import type { GetRequestOptions, IFetchService } from "app/services/fetchService";

import type { CsvExportLeadsColumnConfigDto } from "../../types/columns";

import type { IEnrichmentApiService } from "./types";

const LEADS_ENRICHMENT_API = "/sales-api/leads-enrichment";
const CONTACTS_ENRICHMENT_API = "/sales-api/contacts-enrichment";

const createEnrichmentApiService = (fetchService: Readonly<IFetchService>): IEnrichmentApiService => {
    return {
        fetchLists(options?: Readonly<GetRequestOptions>): Promise<EnrichedState[]> {
            return fetchService.get(`/sales-api/enrichment/list`, undefined, options);
        },

        fetchColumnConfig(options?: Readonly<GetRequestOptions>): Promise<CsvExportLeadsColumnConfigDto> {
            return fetchService.get(`/sales-api/enrichment/description/columns`, undefined, options);
        },

        postCsv: (body: FormData) =>
            fetch(`${swSettings.swsites.sales}${LEADS_ENRICHMENT_API}`, {
                method: "POST",
                body,
                credentials: "include",
            }),

        postContactsCsv: createCancelable(
            (signal: AbortSignal, payload: PostContactsCsvPropsFormData): Promise<string> => {
                return fetchService.post(CONTACTS_ENRICHMENT_API, payload, {
                    cancellation: signal,
                });
            },
        ),
        getEnrichedCsv: createCancelable((signal: AbortSignal, id: string): Promise<string> => {
            const link = `${LEADS_ENRICHMENT_API}/download/${id}`;

            return fetchService.get(link, undefined, {
                cancellation: signal,
            });
        }),
        getSelectedColumnsConfig: createCancelable((signal: AbortSignal): Promise<readonly EnrichmentConfigItem[]> => {
            const link = `${LEADS_ENRICHMENT_API}/columns`;

            return fetchService.get(link, undefined, {
                cancellation: signal,
            });
        }),
        getContactsSelectedColumnsConfig: createCancelable(
            (signal: AbortSignal): Promise<ContactsEnrichmentSelectedColumns> => {
                const link = `${CONTACTS_ENRICHMENT_API}/columns`;

                return fetchService.get(link, undefined, {
                    cancellation: signal,
                });
            },
        ),
        getPreview: createCancelable(
            (
                signal: AbortSignal,
                domains: string[],
            ): Promise<{
                totalRows: 0;
                matchedRows: 0;
            }> => {
                const link = `${LEADS_ENRICHMENT_API}/preview`;

                return fetchService.post(
                    link,
                    { domains },
                    {
                        cancellation: signal,
                    },
                );
            },
        ),
        getEnrichedContactsCsv: createCancelable((signal: AbortSignal, id: string): Promise<string> => {
            const link = `${CONTACTS_ENRICHMENT_API}/download/${id}`;

            return fetchService.get(link, undefined, {
                cancellation: signal,
            });
        }),
        postContactsEnrichmentCsvUploadFailed: (body: FormData) => {
            return fetch(`${swSettings.swsites.sales}${CONTACTS_ENRICHMENT_API}/upload-failed`, {
                method: "POST",
                body,
                credentials: "include",
            });
        },
    };
};

export default createEnrichmentApiService;
